import { useEffect, useState } from "react";
import { api } from "../lib/api";

// Trim "HH:MM:SS" to "HH:MM" so <input type="time"> is happy.
const trimTime = (t) => (t && t.length >= 5 ? t.slice(0, 5) : (t || ""));

/**
 * EventModal — create / edit / delete a single row in `eventcal`.
 * Every authenticated user may write; there is no admin gate.
 */
export default function EventModal({ mode, row, canWrite = true, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    eventcal_dt:  row.eventcal_dt  || "",
    eventcal_frm: trimTime(row.eventcal_frm),
    eventcal_end: trimTime(row.eventcal_end),
    eventcal_ttl: row.eventcal_ttl || "",
    eventcal_dsc: row.eventcal_dsc || "",
    eventcal_url: row.eventcal_url || "",
    repeat_unit: "weeks",
    repeat_count: 1,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Delete flow: 'idle' -> (series only) 'choose' -> 'confirm-one'/'confirm-all' -> deleted.
  // Non-series events skip straight from 'idle' to 'confirm-one'.
  const [delState, setDelState] = useState("idle");

  useEffect(() => {
    setForm((f) => ({
      ...f,
      eventcal_dt:  row.eventcal_dt  || "",
      eventcal_frm: trimTime(row.eventcal_frm),
      eventcal_end: trimTime(row.eventcal_end),
      eventcal_ttl: row.eventcal_ttl || "",
      eventcal_dsc: row.eventcal_dsc || "",
      eventcal_url: row.eventcal_url || "",
    }));
  }, [row]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        eventcal_dt:  form.eventcal_dt,
        eventcal_frm: form.eventcal_frm || null,
        eventcal_end: form.eventcal_end || null,
        eventcal_ttl: form.eventcal_ttl.trim(),
        eventcal_dsc: form.eventcal_dsc?.trim() || null,
        eventcal_url: form.eventcal_url?.trim() || null,
      };
      if (mode === "create") {
        const count = Number(form.repeat_count) || 1;
        if (count > 1) {
          payload.repeat_unit = form.repeat_unit;
          payload.repeat_count = count;
        }
        await api.createEventcal(payload);
      } else {
        await api.updateEventcal(row.eventcal_seq, payload);
      }
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const isSeries = !!row.series_id;

  const clickDelete = () => {
    if (delState === "idle") {
      setDelState(isSeries ? "choose" : "confirm-one");
      return;
    }
    if (delState === "choose") return; // handled by the two choice buttons
    doDelete(delState === "confirm-all" ? "all" : "one");
  };

  const chooseScope = (scope) => setDelState(scope === "all" ? "confirm-all" : "confirm-one");

  const doDelete = async (scope) => {
    setErr(""); setBusy(true);
    try {
      await api.deleteEventcal(row.eventcal_seq, scope);
      onDeleted?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Delete failed");
      setBusy(false);
      setDelState("idle");
    }
  };

  const deleteLabel = delState === "confirm-one" ? "Click again to confirm"
    : delState === "confirm-all" ? "Click again to confirm all"
    : "Delete";

  const title = mode === "create"
    ? "New event"
    : `Edit event · ${row.eventcal_ttl || `#${row.eventcal_seq}`}`;

  const valid = form.eventcal_dt && form.eventcal_ttl.trim();

  return (
    <div
      className="modal-backdrop"
      data-testid="event-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
        data-testid="event-modal-dialog"
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            data-testid="event-modal-close"
            onClick={onClose}
          >×</button>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <div className="form-grid">
            <div className="form-field">
              <label>Date <span className="req">*</span></label>
              <input
                data-testid="event-modal-dt"
                type="date"
                value={form.eventcal_dt}
                required
                onChange={(e) => set("eventcal_dt", e.target.value)}
                disabled={busy || !canWrite}
              />
            </div>
            <div className="form-field">
              <label>From</label>
              <input
                data-testid="event-modal-frm"
                type="time"
                value={form.eventcal_frm}
                onChange={(e) => set("eventcal_frm", e.target.value)}
                disabled={busy || !canWrite}
              />
            </div>
            <div className="form-field">
              <label>To</label>
              <input
                data-testid="event-modal-end"
                type="time"
                value={form.eventcal_end}
                onChange={(e) => set("eventcal_end", e.target.value)}
                disabled={busy || !canWrite}
              />
            </div>
            <div className="form-field form-field-wide" style={{ gridColumn: "span 3" }}>
              <label>Title <span className="req">*</span></label>
              <input
                data-testid="event-modal-ttl"
                type="text"
                maxLength={50}
                value={form.eventcal_ttl}
                onChange={(e) => set("eventcal_ttl", e.target.value)}
                required
                disabled={busy || !canWrite}
              />
            </div>
            {mode === "create" && (
              <>
                <div className="form-field">
                  <label>Repeats</label>
                  <select
                    data-testid="event-modal-repeat-unit"
                    value={form.repeat_unit}
                    onChange={(e) => set("repeat_unit", e.target.value)}
                    disabled={busy || Number(form.repeat_count) <= 1}
                  >
                    <option value="days">Calendar Days</option>
                    <option value="weekdays">Weekdays</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Occurrences
                    <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>1-99</span>
                  </label>
                  <input
                    data-testid="event-modal-repeat-count"
                    type="number"
                    min={1}
                    max={99}
                    value={form.repeat_count}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      set("repeat_count", Number.isNaN(n) ? 1 : Math.min(99, Math.max(1, n)));
                    }}
                    disabled={busy}
                  />
                </div>
              </>
            )}
            <div className="form-field form-field-wide" style={{ gridColumn: "span 3" }}>
              <label>Description
                <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                  max 250 chars
                </span>
              </label>
              <textarea
                data-testid="event-modal-dsc"
                className="notes-textarea"
                rows={4}
                maxLength={250}
                value={form.eventcal_dsc}
                onChange={(e) => set("eventcal_dsc", e.target.value)}
                disabled={busy || !canWrite}
              />
            </div>
            <div className="form-field form-field-wide" style={{ gridColumn: "span 3" }}>
              <label>Sign-up URL
                <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                  optional — shows a "SignUp" button on the event
                </span>
              </label>
              <input
                data-testid="event-modal-url"
                type="url"
                placeholder="https://…"
                maxLength={500}
                value={form.eventcal_url}
                onChange={(e) => set("eventcal_url", e.target.value)}
                disabled={busy || !canWrite}
              />
            </div>
          </div>

          {err && (
            <div className="form-error" data-testid="event-modal-error">{err}</div>
          )}

          <div
            className="modal-actions no-divider"
            style={{ justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {mode === "edit" && (
                <a
                  href={api.eventcalIcsUrl(row.eventcal_seq)}
                  download={`event_${row.eventcal_seq}.ics`}
                  className="btn-ghost"
                  data-testid="event-modal-ics"
                  style={{ padding: "8px 16px", textDecoration: "none" }}
                  title="Download an .ics file to add this event to Google, Apple or Outlook calendar"
                >⤓ Add to calendar</a>
              )}
              {mode === "edit" && canWrite && delState !== "choose" && (
                <button
                  type="button"
                  className="btn-danger"
                  data-testid={delState === "idle" ? "event-modal-delete" : "event-modal-confirm-delete"}
                  disabled={busy}
                  onClick={clickDelete}
                  title={delState === "idle" ? "Delete this event" : deleteLabel}
                >{delState === "idle" ? "Delete" : deleteLabel}</button>
              )}
              {mode === "edit" && canWrite && delState === "choose" && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="muted" style={{ fontSize: 12 }}>Delete:</span>
                  <button
                    type="button"
                    className="btn-danger"
                    data-testid="event-modal-delete-one"
                    disabled={busy}
                    onClick={() => chooseScope("one")}
                  >This event</button>
                  <button
                    type="button"
                    className="btn-danger"
                    data-testid="event-modal-delete-all"
                    disabled={busy}
                    onClick={() => chooseScope("all")}
                  >All in series</button>
                  <button
                    type="button"
                    className="btn-ghost"
                    data-testid="event-modal-delete-cancel"
                    disabled={busy}
                    onClick={() => setDelState("idle")}
                  >Cancel</button>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {canWrite && (
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="event-modal-save"
                  disabled={busy || !valid}
                  style={{ width: "auto", padding: "10px 22px", margin: 0 }}
                >{busy ? "Saving…" : (mode === "create" ? "Create" : "Save")}</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
